import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Select,
  Input,
  DatePicker,
  Typography,
  message,
  Tag,
  Popconfirm,
  Row,
  Col,
  Modal,
  Descriptions,
  Form,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  ProductOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { customsAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import apiClient from '../utils/axiosConfig.ts';
import ProductsManagementModal from '../components/ProductsManagementModal.tsx';
import ManualCustomsDocumentModal, { ManualDocumentType } from '../components/ManualCustomsDocumentModal.tsx';
import NationalizeProductsModal from '../components/NationalizeProductsModal.tsx';
import { documentTypeLabel, documentTypeColor } from '../utils/documentTypes.ts';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface CustomsDocument {
  id: number;
  document_type: 'T1' | 'IM7' | 'IM4' | 'DDT' | 'PACKING_LIST' | 'ALTRO';
  document_number: string;
  document_date: string;
  shipment_id?: number;
  shipment_code?: string;
  shipment_description?: string;
  shipment_vehicle_plate?: string;
  invoice_numbers?: string[];
  supplier_id?: number;
  supplier_name?: string;
  transport_type_id?: number;
  transport_type_name?: string;
  warehouse_id: number;
  warehouse_name?: string;
  origin_location?: string;
  destination_location?: string;
  parent_document_id?: number;
  parent_document_number?: string;
  parent_document_type?: string;
  // T1 di riferimento della stessa spedizione (v3.10.0)
  transit_documents?: { id: number; document_number: string; document_date: string | null; origin_location: string | null; destination_location: string | null }[];
  mnr_code?: string;
  customs_status: 'draft' | 'processed' | 'completed';
  is_manual_entry?: boolean;
  manual_entry_reason?: string;
  notes?: string;
  total_quantity_loaded: number;
  total_quantity_remaining: number;
  total_quantity_used: number;
  created_at: string;
  updated_at?: string;
}

const CustomsDocuments: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('customs-documents');
  const [documents, setDocuments] = useState<CustomsDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    document_type: undefined as string | undefined,
    status: undefined as string | undefined,
    warehouse_id: undefined as number | undefined,
    is_manual: undefined as boolean | undefined,
    shipment_id: undefined as number | undefined,
    vehicle_plate: undefined as string | undefined,
    invoice_number: undefined as string | undefined,
    date_from: undefined as string | undefined,
    date_to: undefined as string | undefined,
    search: undefined as string | undefined,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [productsModalVisible, setProductsModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<CustomsDocument | null>(null);
  const [editForm] = Form.useForm();
  const [newDocumentModalVisible, setNewDocumentModalVisible] = useState(false);
  const [nationalizeModalVisible, setNationalizeModalVisible] = useState(false);
  const [newDocumentType, setNewDocumentType] = useState<ManualDocumentType>('IM7');
  const [editSuppliers, setEditSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [editTransportTypes, setEditTransportTypes] = useState<Array<{ id: number; code?: string; description: string }>>([]);
  const [editShipments, setEditShipments] = useState<Array<{ id: number; shipment_code: string; shipment_description?: string }>>([]);
  const [editParentDocs, setEditParentDocs] = useState<Array<{ id: number; document_type: string; document_number: string }>>([]);

  useEffect(() => {
    const loadEditOptions = async () => {
      try {
        const [suppRes, ttRes, shipRes, parentRes] = await Promise.all([
          apiClient.get('/suppliers'),
          apiClient.get('/transport-types/'),
          apiClient.get('/unified-shipments/', { params: { limit: 1000 } }),
          apiClient.get('/customs-documents', { params: { limit: 1000 } })
        ]);
        setEditSuppliers(suppRes.data || []);
        setEditTransportTypes(ttRes.data || []);
        setEditShipments(shipRes.data?.shipments || []);
        setEditParentDocs(parentRes.data?.documents || []);
      } catch (error) {
        console.error('Errore nel caricamento delle opzioni di modifica:', error);
      }
    };
    loadEditOptions();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
        ...filters,
      };

      const response = await customsAPI.getCustomsDocuments(params);

      if (response && response.documents) {
        setDocuments(response.documents);
        setPagination(prev => ({
          ...prev,
          total: response.total,
        }));
      } else {
        console.error('Invalid response structure:', response);
        message.error('Errore nella struttura dati ricevuti');
        setDocuments([]);
      }
    } catch (error) {
      message.error('Errore nel caricamento dei documenti doganali');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [pagination.current, pagination.pageSize]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadDocuments();
  };

  const handleReset = () => {
    setFilters({
      document_type: undefined,
      status: undefined,
      warehouse_id: undefined,
      is_manual: undefined,
      shipment_id: undefined,
      vehicle_plate: undefined,
      invoice_number: undefined,
      date_from: undefined,
      date_to: undefined,
      search: undefined,
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        date_from: dates[0].format('YYYY-MM-DD'),
        date_to: dates[1].format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        date_from: undefined,
        date_to: undefined,
      }));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await customsAPI.deleteCustomsDocument(id);
      message.success('Documento eliminato con successo');
      loadDocuments();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nell\'eliminazione del documento');
    }
  };

  // Elabora: conferma esplicita (fa entrare la merce in magazzino, non si annulla) e
  // blocco del doppio clic mentre la richiesta è in corso
  const [processingId, setProcessingId] = useState<number | null>(null);

  const confirmProcess = (record: CustomsDocument) => {
    Modal.confirm({
      title: `Elaborare il documento ${record.document_number}?`,
      content: "La merce del documento entra in magazzino (da 'in transito' a 'disponibile'). L'operazione non si può annullare.",
      okText: 'Elabora',
      cancelText: 'Annulla',
      onOk: () => handleProcess(record.id),
    });
  };

  const handleProcess = async (id: number) => {
    if (processingId !== null) return;
    setProcessingId(id);
    try {
      await customsAPI.processCustomsDocument(id);
      message.success('Documento elaborato con successo: la merce è entrata in magazzino');
      loadDocuments();
    } catch (error: any) {
      // il motivo reale (es. quantità del documento diverse dalla merce in transito)
      message.error(error?.response?.data?.detail || 'Errore nell\'elaborazione del documento', 10);
    } finally {
      setProcessingId(null);
    }
  };

  const handleView = async (document: CustomsDocument) => {
    try {
      const fullDocument = await customsAPI.getCustomsDocument(document.id);
      setSelectedDocument(fullDocument);
      setViewModalVisible(true);
    } catch (error) {
      message.error('Errore nel caricamento dei dettagli del documento');
    }
  };

  const handleEdit = async (document: CustomsDocument) => {
    try {
      const fullDocument = await customsAPI.getCustomsDocument(document.id);
      setSelectedDocument(fullDocument);
      editForm.setFieldsValue({
        document_number: fullDocument.document_number,
        document_date: dayjs(fullDocument.document_date),
        notes: fullDocument.notes || '',
        supplier_id: fullDocument.supplier_id ?? undefined,
        shipment_id: fullDocument.shipment_id ?? undefined,
        transport_type_id: fullDocument.transport_type_id ?? undefined,
        origin_location: fullDocument.origin_location || '',
        destination_location: fullDocument.destination_location || '',
        parent_document_id: fullDocument.parent_document_id ?? undefined,
        mnr_code: fullDocument.mnr_code || ''
      });
      setEditModalVisible(true);
    } catch (error) {
      message.error('Errore nel caricamento dei dettagli del documento');
    }
  };

  const handleEditSave = async (values: any) => {
    if (!selectedDocument) return;

    try {
      const updateData = {
        document_number: values.document_number,
        document_date: values.document_date.format('YYYY-MM-DD'),
        notes: values.notes,
        supplier_id: values.supplier_id ?? null,
        shipment_id: values.shipment_id ?? null,
        transport_type_id: values.transport_type_id ?? null,
        origin_location: values.origin_location || null,
        destination_location: values.destination_location || null,
        parent_document_id: values.parent_document_id ?? null,
        mnr_code: values.mnr_code || null
      };

      await customsAPI.updateCustomsDocument(selectedDocument.id, updateData);
      message.success('Documento aggiornato con successo');
      setEditModalVisible(false);
      loadDocuments();
    } catch (error) {
      message.error('Errore nell\'aggiornamento del documento');
    }
  };

  const handleManageProducts = (document: CustomsDocument) => {
    setSelectedDocument(document);
    setProductsModalVisible(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'processed': return 'orange';
      case 'completed': return 'green';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return 'Bozza';
      case 'processed': return 'Elaborato';
      case 'completed': return 'Completato';
      default: return status;
    }
  };

  const getDocumentTypeLabel = (type: string) => documentTypeLabel(type);

  const getDocumentTypeColor = (type: string) => documentTypeColor(type);

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'document_type',
      key: 'document_type',
      render: (type: string) => (
        <Tag color={getDocumentTypeColor(type)}>{getDocumentTypeLabel(type)}</Tag>
      ),
    },
    {
      title: 'Numero Documento',
      dataIndex: 'document_number',
      key: 'document_number',
    },
    {
      title: 'Data',
      dataIndex: 'document_date',
      key: 'document_date',
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'Fornitore',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (name: string) => name || '-',
    },
    {
      title: 'Spedizione',
      dataIndex: 'shipment_code',
      key: 'shipment_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Targa/N° Container',
      dataIndex: 'shipment_vehicle_plate',
      key: 'shipment_vehicle_plate',
      render: (plate: string) => plate || '-',
    },
    {
      title: 'Fattura Fornitore',
      dataIndex: 'invoice_numbers',
      key: 'invoice_numbers',
      render: (numbers: string[] | undefined) => (numbers && numbers.length ? numbers.join(', ') : '-'),
    },
    {
      title: 'Magazzino',
      dataIndex: 'warehouse_name',
      key: 'warehouse_name',
    },
    {
      title: 'Origine → Destinazione',
      key: 'locations',
      render: (_: any, record: CustomsDocument) => (
        <div>
          {record.origin_location || '-'} → {record.destination_location || record.warehouse_name || '-'}
        </div>
      ),
    },
    {
      title: 'Quantità',
      key: 'quantities',
      render: (_: any, record: CustomsDocument) => (
        <Space direction="vertical" size="small">
          <div>
            <Tag color="blue">{record.total_quantity_loaded}</Tag>
            <span style={{ marginLeft: 4 }}>Caricate</span>
          </div>
          <div>
            <Tag color="orange">{record.total_quantity_remaining}</Tag>
            <span style={{ marginLeft: 4 }}>In magazzino</span>
          </div>
          <div>
            <Tag color="green">{record.total_quantity_used}</Tag>
            <span style={{ marginLeft: 4 }}>Usciti</span>
          </div>
        </Space>
      ),
    },
    {
      title: 'Stato',
      dataIndex: 'customs_status',
      key: 'customs_status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
      filters: [
        { text: 'Bozza', value: 'draft' },
        { text: 'Elaborato', value: 'processed' },
        { text: 'Completato', value: 'completed' },
      ],
    },
    {
      title: 'Tipo',
      key: 'entry_type',
      render: (_: any, record: CustomsDocument) => (
        record.is_manual_entry ?
          <Tag color="purple">Manuale</Tag> :
          <Tag color="cyan">Automatico</Tag>
      ),
    },
    {
      title: 'Creato il',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: true,
    },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_: any, record: CustomsDocument) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            title="Visualizza"
            onClick={() => handleView(record)}
          />
          {canEdit && record.document_type !== 'T1' && (
            <Button
              type="text"
              icon={<ProductOutlined />}
              size="small"
              title="Gestisci Prodotti"
              onClick={() => handleManageProducts(record)}
            />
          )}
          {canEdit && record.document_type !== 'T1' && (
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              title="Modifica"
              disabled={record.customs_status === 'completed'}
              onClick={() => handleEdit(record)}
            />
          )}
          {canEdit && record.customs_status === 'draft' && (
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              size="small"
              title="Elabora"
              loading={processingId === record.id}
              disabled={processingId !== null}
              onClick={() => confirmProcess(record)}
            />
          )}
          {canEdit && (
            <Popconfirm
              title="Sei sicuro di voler eliminare questo documento?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                title="Elimina"
                disabled={record.customs_status !== 'draft'}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>
          📄 Documenti di Carico
        </Title>
        <Space>
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setNewDocumentType('IM7');
                setNewDocumentModalVisible(true);
              }}
            >
              Nuovo H2 (IM7)
            </Button>
          )}
          {canEdit && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setNewDocumentType('IM4');
                setNewDocumentModalVisible(true);
              }}
            >
              Nuovo H1 (IM4)
            </Button>
          )}
          {canEdit && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setNewDocumentType('DDT');
                setNewDocumentModalVisible(true);
              }}
            >
              Nuovo DDT / Packing List / Altro
            </Button>
          )}
          {canEdit && (
            <Button
              icon={<SafetyCertificateOutlined />}
              onClick={() => setNationalizeModalVisible(true)}
            >
              Nazionalizza Prodotti
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={loadDocuments}>
            Aggiorna
          </Button>
        </Space>
      </div>

      {/* Filtri */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <label>Tipo Documento:</label>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Tutti i tipi"
              allowClear
              value={filters.document_type}
              onChange={(value) => setFilters(prev => ({ ...prev, document_type: value }))}
            >
              <Option value="IM7">H2 (IM7)</Option>
              <Option value="IM4">H1 (IM4)</Option>
              <Option value="DDT">DDT</Option>
              <Option value="PACKING_LIST">Packing List</Option>
              <Option value="ALTRO">Altro</Option>
              <Option value="T1">T1</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <label>Stato:</label>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Tutti gli stati"
              allowClear
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="draft">Bozza</Option>
              <Option value="processed">Elaborato</Option>
              <Option value="completed">Completato</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <label>Tipo Caricamento:</label>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Tutti"
              allowClear
              value={filters.is_manual}
              onChange={(value) => setFilters(prev => ({ ...prev, is_manual: value }))}
            >
              <Option value={true}>Manuale</Option>
              <Option value={false}>Automatico</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <label>Periodo:</label>
            <RangePicker
              style={{ width: '100%', marginTop: 4 }}
              format="DD/MM/YYYY"
              placeholder={['Data da', 'Data a']}
              onChange={handleDateRangeChange}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <label>Spedizione:</label>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Tutte le spedizioni"
              allowClear
              showSearch
              optionFilterProp="label"
              value={filters.shipment_id}
              onChange={(value) => setFilters(prev => ({ ...prev, shipment_id: value }))}
              options={editShipments.map(s => ({
                value: s.id,
                label: `${s.shipment_code}${s.shipment_description ? ' - ' + s.shipment_description : ''}`,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label>Targa / N° Container:</label>
            <Input.Search
              placeholder="Targa o container (spazi ignorati)..."
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              value={filters.vehicle_plate}
              onSearch={(value) => {
                setFilters(prev => ({ ...prev, vehicle_plate: value || undefined }));
                handleSearch();
              }}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, vehicle_plate: e.target.value || undefined }));
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label>Fattura / Packing list fornitore:</label>
            <Input.Search
              placeholder="Numero fattura o packing list..."
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              value={filters.invoice_number}
              onSearch={(value) => {
                setFilters(prev => ({ ...prev, invoice_number: value || undefined }));
                handleSearch();
              }}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, invoice_number: e.target.value || undefined }));
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={10}>
            <label>Ricerca libera:</label>
            <Input.Search
              placeholder="Cerca per numero documento, fornitore, magazzino, codice prodotto..."
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              value={filters.search}
              onSearch={(value) => {
                setFilters(prev => ({ ...prev, search: value || undefined }));
                handleSearch();
              }}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, search: e.target.value || undefined }));
              }}
            />
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Cerca
            </Button>
            <Button onClick={handleReset}>
              Reset Filtri
            </Button>
          </Space>
        </div>
      </Card>

      {/* Tabella */}
      <Card>
        <Table
          columns={columns}
          dataSource={documents}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} di ${total} documenti`,
          }}
          onChange={(pag) => {
            setPagination({
              current: pag.current || 1,
              pageSize: pag.pageSize || 20,
              total: pagination.total,
            });
          }}
          size="small"
        />
      </Card>

      {/* View Modal */}
      <Modal
        title="Dettagli Documento Doganale"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Chiudi
          </Button>
        ]}
        width={800}
      >
        {selectedDocument && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Tipo">
              <Tag color={getDocumentTypeColor(selectedDocument.document_type)}>{getDocumentTypeLabel(selectedDocument.document_type)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Numero">{selectedDocument.document_number}</Descriptions.Item>
            <Descriptions.Item label="Data">{dayjs(selectedDocument.document_date).format('DD/MM/YYYY')}</Descriptions.Item>
            <Descriptions.Item label="Stato">
              <Tag color={getStatusColor(selectedDocument.customs_status)}>
                {getStatusText(selectedDocument.customs_status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Codice MNR">{selectedDocument.mnr_code || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tipo Caricamento">
              {selectedDocument.is_manual_entry ?
                <Tag color="purple">Manuale</Tag> :
                <Tag color="cyan">Automatico</Tag>
              }
            </Descriptions.Item>
            <Descriptions.Item label="Fornitore">{selectedDocument.supplier_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Magazzino">{selectedDocument.warehouse_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Origine">{selectedDocument.origin_location || '-'}</Descriptions.Item>
            <Descriptions.Item label="Destinazione">{selectedDocument.destination_location || '-'}</Descriptions.Item>
            <Descriptions.Item label="Spedizione">
              {selectedDocument.shipment_code
                ? `${selectedDocument.shipment_code}${selectedDocument.shipment_description ? ' - ' + selectedDocument.shipment_description : ''}`
                : '-'
              }
            </Descriptions.Item>
            <Descriptions.Item label="Tipo Trasporto">{selectedDocument.transport_type_name || '-'}</Descriptions.Item>
            {(selectedDocument.transit_documents?.length ?? 0) > 0 && (
              <Descriptions.Item label="T1 della spedizione" span={2}>
                {selectedDocument.transit_documents!.map((t) => (
                  <div key={t.id}>
                    <Tag color={getDocumentTypeColor('T1')}>T1</Tag> {t.document_number}
                    {t.document_date ? ` del ${dayjs(t.document_date).format('DD/MM/YYYY')}` : ''}
                    {t.origin_location || t.destination_location ? ` (${t.origin_location || '?'} → ${t.destination_location || '?'})` : ''}
                  </div>
                ))}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Documento Padre">
              {selectedDocument.parent_document_number
                ? <><Tag color={getDocumentTypeColor(selectedDocument.parent_document_type || '')}>{getDocumentTypeLabel(selectedDocument.parent_document_type || '')}</Tag> {selectedDocument.parent_document_number}</>
                : '-'
              }
            </Descriptions.Item>
            <Descriptions.Item label="Quantità Caricata">
              <Tag color="blue">{selectedDocument.total_quantity_loaded}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Quantità Rimanente">
              <Tag color="orange">{selectedDocument.total_quantity_remaining}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Quantità Utilizzata">
              <Tag color="green">{selectedDocument.total_quantity_used}</Tag>
            </Descriptions.Item>
            {selectedDocument.is_manual_entry && (
              <Descriptions.Item label="Motivo Manuale" span={2}>
                {selectedDocument.manual_entry_reason || '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Note" span={2}>
              {selectedDocument.notes || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Creato il">
              {dayjs(selectedDocument.created_at).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Aggiornato il">
              {selectedDocument.updated_at ? dayjs(selectedDocument.updated_at).format('DD/MM/YYYY HH:mm') : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Modifica Documento Doganale"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        okText="Salva"
        cancelText="Annulla"
        width="80vw"
        style={{ top: 30 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSave}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="document_number"
                label="Numero Documento"
                rules={[{ required: true, message: 'Inserisci il numero del documento' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="document_date"
                label="Data Documento"
                rules={[{ required: true, message: 'Inserisci la data del documento' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="supplier_id" label="Fornitore (opzionale)">
                <Select
                  placeholder="Seleziona fornitore"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                >
                  {editSuppliers.map(s => (
                    <Option key={s.id} value={s.id}>{s.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shipment_id" label="Spedizione (opzionale)">
                <Select
                  placeholder="Seleziona spedizione"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                >
                  {editShipments.map(s => (
                    <Option key={s.id} value={s.id}>
                      {s.shipment_code}{s.shipment_description ? ` - ${s.shipment_description}` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="transport_type_id" label="Tipo Trasporto (opzionale)">
                <Select
                  placeholder="Seleziona tipo trasporto"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                >
                  {editTransportTypes.map(t => (
                    <Option key={t.id} value={t.id}>
                      {t.code ? `${t.code} - ${t.description}` : t.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mnr_code" label="Codice MNR (opzionale)">
                <Input placeholder="es. MNR123" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="origin_location" label="Origine (opzionale)">
                <Input placeholder="Località di origine" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="destination_location" label="Destinazione (opzionale)">
                <Input placeholder="Località di destinazione" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="parent_document_id" label="Documento Padre (opzionale)">
                <Select
                  placeholder="Seleziona documento padre"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                >
                  {editParentDocs
                    .filter(d => d.id !== selectedDocument?.id)
                    .map(d => (
                      <Option key={d.id} value={d.id}>
                        {getDocumentTypeLabel(d.document_type)} - {d.document_number}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Note"
          >
            <Input.TextArea rows={3} placeholder="Note aggiuntive..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Products Management Modal */}
      <ProductsManagementModal
        visible={productsModalVisible}
        onCancel={() => setProductsModalVisible(false)}
        documentId={selectedDocument?.id || 0}
        documentStatus={selectedDocument?.customs_status || 'draft'}
        documentType={selectedDocument?.document_type || ''}
        shipmentId={selectedDocument?.shipment_id ?? null}
        onSuccess={() => {
          loadDocuments();
        }}
      />

      {/* New Customs Document Modal */}
      <ManualCustomsDocumentModal
        visible={newDocumentModalVisible}
        documentType={newDocumentType}
        onCancel={() => setNewDocumentModalVisible(false)}
        onSuccess={() => {
          setNewDocumentModalVisible(false);
          loadDocuments();
        }}
      />

      {/* Nazionalizza Prodotti: sdoganamento con IM4 di merce già in magazzino doganale */}
      <NationalizeProductsModal
        visible={nationalizeModalVisible}
        onCancel={() => setNationalizeModalVisible(false)}
        onSuccess={() => {
          setNationalizeModalVisible(false);
          loadDocuments();
        }}
      />
    </div>
  );
};

export default CustomsDocuments;